import * as Sentry from '@sentry/browser';
import { LimitedCache } from 'limited-cache';
import { type User } from '../global-state/user';
import { exposeOnWindow } from './helpers';
import { platform } from './platform';
import { stripConsoleLogPrefix } from './pretty-logs';

const isSentryEnabled = !import.meta.env.DEV && typeof window !== 'undefined';
const envName =
  typeof window === 'undefined'
    ? 'ssr'
    : location.hostname === 'www.adblockpodcast.com'
      ? 'prod'
      : location.hostname.endsWith('vercel.app')
        ? 'preview'
        : location.hostname === 'localhost'
          ? 'dev'
          : location.hostname;

export function initSentry() {
  Sentry.init({
    dsn: 'https://9175cefa001d58dacc2be6cb7ff99188@sen.adblockpodcast.com/6',
    sendDefaultPii: true,
    enabled: isSentryEnabled,
    beforeBreadcrumb: transformBreadcrumb,
    environment: envName,
    normalizeDepth: 7,
  });
  setSentryTag('platform', platform);
}

// Throttle Sentry errors. Never send more than 10 messages within an hour timeframe
const maxDuplicateErrorsPerInterval = 10;
const duplicateErrorIntervalMs = 1000 * 60 * 60; // Reset every hour
const recentlyReportedErrorMessages = LimitedCache<number>({
  maxCacheTime: duplicateErrorIntervalMs,
});

export function reportSentryError(
  error: Error,
  context?: Record<string, unknown>
  // fingerprint?: ('{{ default }}' | AnyOtherString)[] // Enable if needed
) {
  // Only send so many duplicate errors to Sentry within a given interval
  const errorMessage = '' + error;
  const reportsInPastHour = recentlyReportedErrorMessages.get(errorMessage) || 0;
  if (reportsInPastHour >= maxDuplicateErrorsPerInterval) {
    console.log(`Not reporting duplicate Sentry error (> ${reportsInPastHour})`);
    return;
  }
  recentlyReportedErrorMessages.set(errorMessage, reportsInPastHour + 1);

  context = typeof context === 'string' ? { context } : context;
  console.error(`Reporting Sentry error:`, error, context);

  Sentry.withScope((scope) => {
    // if (fingerprint) scope.setFingerprint(fingerprint);
    Sentry.captureException(error, { extra: context });
  });
}

export function reportSentryWarning(
  error: Error,
  context?: Record<string, unknown>
  // fingerprint?: ('{{ default }}' | AnyOtherString)[] // Enable if needed
) {
  // Only send so many duplicate errors to Sentry within a given interval
  const errorMessage = '' + error;
  const reportsInPastHour = recentlyReportedErrorMessages.get(errorMessage) || 0;
  if (reportsInPastHour >= maxDuplicateErrorsPerInterval) {
    console.log(`Not reporting duplicate Sentry warning (> ${reportsInPastHour})`);
    return;
  }
  recentlyReportedErrorMessages.set(errorMessage, reportsInPastHour + 1);

  context = typeof context === 'string' ? { context } : context;
  console.warn(`Reporting Sentry warning:`, error, context);

  Sentry.withScope((scope) => {
    scope.setLevel('warning');
    // if (fingerprint) scope.setFingerprint(fingerprint);
    Sentry.captureException(error, { extra: context });
  });
}

export function setSentryUser(user: User | null) {
  Sentry.setUser(user ? { id: user.id, email: user.email, name: user.name } : null);
}

export function setSentryTag(key: string, value: string) {
  Sentry.setTag(key, value);
}

export function getMessage(error: Error | unknown, fallback?: string) {
  if (error instanceof Error) {
    return error.message;
  }
  return fallback || '' + error;
}

// Strip console.log prefix from Sentry breadcrumbs so they're easier to read
function transformBreadcrumb(breadcrumb: Sentry.Breadcrumb) {
  if (breadcrumb.category === 'console' && breadcrumb.message?.startsWith('%c')) {
    const { name, content } = stripConsoleLogPrefix(breadcrumb.message);
    return {
      ...breadcrumb,
      message: `[${name}] ${content}`,
      data: {
        ...breadcrumb.data,
        arguments: breadcrumb.data?.arguments?.slice(2),
      },
    };
  }
  return breadcrumb;
}

// Silly, but this way we can use it within localstorage.ts without circular dependency issues
exposeOnWindow({ reportSentryError });
